"use client";

import Image from "next/image";

import { cn } from "@/shared/lib/utils/cn";

interface AiLoaderProps {
  label?: string;
  /** Show only the · · · typing dots (no text label). */
  dotsOnly?: boolean;
  size?: "xs" | "sm" | "lg";
  /** shadcn shimmer sweep on the label (live status pills). */
  labelShimmer?: boolean;
  className?: string;
}

function labelText(label: string) {
  return label.replace(/(?:\u2026|\.{3})\s*$/u, "").trimEnd();
}

/**
 * Animated Aunix spark + optional label/dots.
 * Already includes the avatar — do not also render `AgentAvatar` next to it.
 */
export function AiLoader({
  label,
  dotsOnly = false,
  size = "sm",
  labelShimmer = false,
  className,
}: AiLoaderProps) {
  const compact = size === "xs";
  const large = size === "lg";
  const text = label ? labelText(label) : "";
  const showLabel = Boolean(text) && !dotsOnly;
  const showDots = dotsOnly || (showLabel && !compact);

  return (
    <div
      role="status"
      aria-label={label ?? (dotsOnly ? "Working" : "Processing")}
      className={cn("inline-flex items-center gap-2.5", className)}
    >
      <span
        className={cn(
          "relative grid shrink-0 place-items-center transition-[width,height] duration-700",
          compact ? "size-4" : large ? "size-12" : "size-8",
        )}
      >
        <span className="absolute -inset-1 rounded-full bg-primary/10 motion-safe:animate-ping [animation-duration:2.4s]" />
        <span className="absolute -inset-0.5 rounded-full border border-primary/20 border-t-primary/80 [animation:spin_1.8s_linear_infinite]" />
        <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_35%_30%,color-mix(in_oklch,var(--primary)_55%,white)_0%,var(--primary)_58%,color-mix(in_oklch,var(--primary)_70%,black)_100%)] shadow-[0_0_14px_color-mix(in_oklch,var(--primary)_35%,transparent)]" />
        <span className="absolute -top-0.5 left-1/2 size-1 -translate-x-1/2 rounded-full bg-white shadow-[0_0_6px_2px_rgba(255,255,255,0.75)] [animation:pulse_1.2s_ease-in-out_infinite]" />
        <Image
          src="/figma-assets/aunix-spark.svg"
          alt=""
          width={compact ? 8 : large ? 24 : 16}
          height={compact ? 8 : large ? 24 : 16}
          className="relative z-10 transition-[width,height] duration-700"
        />
      </span>

      {showLabel || showDots ? (
        <span className={cn("flex min-w-0 items-center gap-1.5", compact && "flex-1")}>
          {showLabel ? (
            <span
              className={cn(
                "min-w-0 font-medium transition-[font-size] duration-700",
                labelShimmer
                  ? cn(
                      "shimmer truncate whitespace-nowrap text-muted-foreground",
                      compact ? "text-[11px]" : large ? "text-sm" : "text-[11px]",
                    )
                  : compact
                    ? "truncate whitespace-nowrap text-[11px] text-foreground"
                    : "bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent [animation:pulse_2.2s_ease-in-out_infinite]",
                !labelShimmer && !compact && (large ? "text-sm" : "text-[11px]"),
              )}
            >
              {text}
            </span>
          ) : null}
          {showDots ? (
            <span className="agent-typing-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/** Live agent wait — spark + Thinking with circular wave dots. */
export function AgentThinking({ className }: { className?: string }) {
  return (
    <div
      className={cn("thinking-reveal flex items-center gap-3 pt-2", className)}
      role="status"
      aria-label="Thinking"
    >
      <AiLoader size="sm" />
      <span className="inline-flex items-center text-[13px] leading-none text-muted-foreground">
        Thinking
        <span className="agent-typing-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </span>
    </div>
  );
}

/** Default working wait — spark + same circular wave dots as Thinking. */
export function AgentWorkingLoader({ className }: { className?: string }) {
  return (
    <div
      className={cn("thinking-reveal flex items-center gap-3 pt-2", className)}
      role="status"
      aria-label="Working"
    >
      <AiLoader size="sm" dotsOnly className="gap-3" />
    </div>
  );
}
